import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, X } from "lucide-react";
import { useDataProvider, useGetList, useNotify, useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { parseChecklistSteps } from "./checklistSteps";
import {
  addChecklistSteps,
  fullName,
  type ChecklistItem,
  useTeam,
} from "./taskData";

const formatTickedAt = (value: string) =>
  new Date(value).toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Rome",
  });

// Steps of a task: tick with one tap, add (pasting several lines adds several
// steps), reorder with up and down, remove. The database stamps who ticked a
// step and when (D-028): the app only sends done_at set or cleared.
export const TaskChecklist = ({ taskId }: { taskId: number }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const { data: team = [] } = useTeam();
  const { data: items = [], isPending } = useGetList<ChecklistItem>(
    "task_checklist_items",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "position", order: "ASC" },
      filter: { task_id: taskId },
    },
  );
  const [draft, setDraft] = useState("");

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["task_checklist_items"] });

  const run = async (action: () => Promise<unknown>) => {
    try {
      await action();
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), {
        type: "error",
      });
    }
    await refresh();
  };

  const toggle = (item: ChecklistItem) =>
    run(() =>
      dataProvider.update("task_checklist_items", {
        id: item.id,
        data: { done_at: item.done_at ? null : new Date().toISOString() },
        previousData: item,
      }),
    );

  const move = (index: number, offset: -1 | 1) => {
    const other = items[index + offset];
    const item = items[index];
    if (!other) return;
    return run(async () => {
      await dataProvider.update("task_checklist_items", {
        id: item.id,
        data: { position: other.position },
        previousData: item,
      });
      await dataProvider.update("task_checklist_items", {
        id: other.id,
        data: { position: item.position },
        previousData: other,
      });
    });
  };

  const remove = (item: ChecklistItem) =>
    run(() =>
      dataProvider.delete("task_checklist_items", {
        id: item.id,
        previousData: item,
      }),
    );

  const add = (event: React.FormEvent) => {
    event.preventDefault();
    const labels = parseChecklistSteps(draft);
    if (labels.length === 0) return;
    const nextPosition = Math.max(0, ...items.map((item) => item.position)) + 1;
    setDraft("");
    return run(() => addChecklistSteps(taskId, labels, nextPosition));
  };

  const doneCount = items.filter((item) => item.done_at).length;
  const memberName = (id: number | null) =>
    fullName(team.find((member) => member.id === id));

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between">
        <h2 className="text-lg font-semibold">
          {translate("tenax.tasks.checklist.title")}
        </h2>
        {items.length > 0 && (
          <span className="text-muted-foreground text-sm">
            {translate("tenax.tasks.checklist.progress", {
              done: doneCount,
              total: items.length,
            })}
          </span>
        )}
      </div>

      {!isPending && items.length === 0 && (
        <p className="text-muted-foreground text-sm">
          {translate("tenax.tasks.checklist.empty")}
        </p>
      )}

      <ul className="flex flex-col divide-y">
        {items.map((item, index) => (
          <li key={item.id} className="flex items-center gap-2 py-2">
            <label className="flex flex-1 items-start gap-3 min-h-10 cursor-pointer">
              <Checkbox
                className="mt-1 size-5"
                checked={!!item.done_at}
                onCheckedChange={() => toggle(item)}
                aria-label={item.label}
              />
              <span className="flex flex-col">
                <span
                  className={
                    item.done_at ? "line-through text-muted-foreground" : ""
                  }
                >
                  {item.label}
                </span>
                {item.done_at && (
                  <span className="text-muted-foreground text-xs">
                    {translate("tenax.tasks.checklist.ticked_by", {
                      name: memberName(item.done_by),
                      date: formatTickedAt(item.done_at),
                    })}
                  </span>
                )}
              </span>
            </label>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={index === 0}
              onClick={() => move(index, -1)}
              aria-label={translate("tenax.tasks.checklist.move_up")}
            >
              <ArrowUp />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={index === items.length - 1}
              onClick={() => move(index, 1)}
              aria-label={translate("tenax.tasks.checklist.move_down")}
            >
              <ArrowDown />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => remove(item)}
              aria-label={translate("tenax.tasks.checklist.remove")}
            >
              <X />
            </Button>
          </li>
        ))}
      </ul>

      <form onSubmit={add} className="flex flex-col gap-2">
        <Textarea
          rows={2}
          value={draft}
          placeholder={translate("tenax.tasks.checklist.add_placeholder")}
          aria-label={translate("tenax.tasks.checklist.add")}
          onChange={(event) => setDraft(event.target.value)}
        />
        <Button type="submit" variant="outline" disabled={!draft.trim()}>
          {translate("tenax.tasks.checklist.add")}
        </Button>
      </form>
    </section>
  );
};
